import { Document } from '@startuptools/business-ui/document/Document';
import { type ComponentProps, Suspense, useEffect, useState } from 'react';
import { DATE_FORMAT_LUXON } from '@startuptools/common/common';
import { DateTime } from 'luxon';
import { useCompanyId } from '@startuptools/common/react-wrapper';
import {
  type GqlDocGenValidateMeetingQuery,
  useDocGenValidateMeetingLazyQuery,
  useDocGenerateMeetingSummonsMutation,
  useFileQuery,
} from '../../../../graphql/react-operations';
import { apiClient } from '../../../../common/http';
import { hasValidatorErrors } from '../../utils';
import { MeetingValidatorErrorDialog } from '../MeetingValidatorErrorDialog';
import {
  GqlDocGenState,
  type GqlBolagoFile,
  type GqlMeeting,
  GqlMeetingKind,
} from '../../../../graphql/base-types.graphql';
import { isNil, omit } from 'lodash-es';

const meetingFilename = (meeting: Pick<GqlMeeting, 'startTime' | 'kind' | 'number'>): string => {
  const meetingDate = DateTime.fromISO(meeting.startTime).toFormat(DATE_FORMAT_LUXON);
  const filename = {
    [GqlMeetingKind.BoardMeeting]: `Kallelse - Styrelsemöte #${meeting.number} - ${meetingDate}`,
    [GqlMeetingKind.ExtraShareholdersMeeting]: `Kallelse - Extra bolagsstämma - ${meetingDate}`,
    [GqlMeetingKind.ShareholdersMeeting]: `Kallelse - Bolagsstämma - ${meetingDate}`,
  }[meeting.kind];
  return filename;
};

const MeetingSummonsDocumentInner = ({
  meeting,
  refetch,
}: {
  meeting: Pick<GqlMeeting, 'id' | 'number' | 'startTime' | 'kind'> & {
    latestSummonsDocument?: Pick<GqlBolagoFile, 'id'> | null;
  };
  refetch: () => void;
}) => {
  const companyId = useCompanyId();

  const [validationErrors, setValidationErrors] = useState<
    GqlDocGenValidateMeetingQuery['docGenValidateMeeting'] | null
  >(null);

  const defaultFilename = meetingFilename(meeting);

  const { data, stopPolling } = useFileQuery({
    variables: { id: meeting.latestSummonsDocument?.id || '' },
    skip: isNil(meeting.latestSummonsDocument),
    pollInterval: 2000,
    fetchPolicy: 'cache-and-network',
  });

  const [doGetValidate] = useDocGenValidateMeetingLazyQuery({
    variables: { companyId, meetingId: meeting.id },
    fetchPolicy: 'no-cache',
  });
  // eslint-disable-next-line sut/loading-mutation
  const [doGenerate] = useDocGenerateMeetingSummonsMutation();

  useEffect(() => {
    if (data?.file && [GqlDocGenState.Failed, GqlDocGenState.Completed].includes(data.file.genState)) {
      stopPolling();
    }
  }, [data?.file, stopPolling]);

  return (
    <>
      <Document
        defaultFileName={defaultFilename}
        document={data?.file}
        allowDigitalSignature={false}
        validate={async () => {
          const { data } = await doGetValidate();
          if (!data) {
            return { isValid: false, errors: undefined };
          }
          const hasMeetingErrors = hasValidatorErrors(
            omit(data.docGenValidateMeeting.validatorErrors, 'protocol', 'votingList'),
          );
          const hasAppendiceErrors = data.docGenValidateMeeting.appendices.some(a =>
            a.validatorStatuses.some(vs => vs.hasError || !vs.completed),
          );
          if (hasMeetingErrors || hasAppendiceErrors) {
            setValidationErrors(data.docGenValidateMeeting);
            return { isValid: false, errors: undefined };
          }
          return { isValid: true, errors: undefined };
        }}
        preview={async () => {
          return apiClient.previewMeetingSummons(companyId, meeting.id);
        }}
        generate={async () => {
          await doGenerate({ variables: { companyId, meetingId: meeting.id } });
          refetch();
        }}
      />

      {validationErrors && (
        <MeetingValidatorErrorDialog
          onClose={() => setValidationErrors(null)}
          meeting={meeting}
          meetingValidatorErrors={validationErrors}
        />
      )}
    </>
  );
};

export const MeetingSummonsDocument = (props: ComponentProps<typeof MeetingSummonsDocumentInner>) => {
  return (
    <Suspense>
      <MeetingSummonsDocumentInner {...props} />
    </Suspense>
  );
};
